import { activeRecordingTransportAtom } from '@/atoms/chronicle';
import { chronicleSessionIdAtom } from '@/atoms/chronicle/session';
import { activeSessionIdAtom } from '@/atoms/liveTranscriptionAtom';
import { offlineCaptureRunningAtom } from '@/atoms/offline-recording';
import { stopSharedRecordingTimer } from '@/models/timerModel';
import { type SessionEndSweepResult } from '@/tauri/online-chunk-upload/session-end-sweep';
import { logger } from '@/utils/logger/logger';
import { RecordingStatus } from '@heidi/schema/ml-scribe';
import { GET_SESSION_BY_ID, type SessionResponse } from '@heidi/session/contracts';
import { type QueryClient } from '@tanstack/react-query';
import { type getDefaultStore } from 'jotai';

/**
 * Release the frontend's "a recording is in progress" claim once a session-end
 * sweep has confirmed a stop.
 *
 * WHY THIS IS NOT OPTIONAL POLISH. `sweepCaptureOnSessionEnd` terminates NATIVE
 * capture and deliberately writes no UI state, which left every frontend layer
 * still claiming a live recording after a successful sweep. Two harms, and the
 * second is the worse one:
 *
 *  1. Surfaces lie. The timer keeps running, the nav guard keeps blocking, the
 *     record button keeps rendering a recording that no longer exists.
 *  2. `timerModel.isRunning` left true DISABLES the last-resort force-stop.
 *     `runRecorderSafetyStop` (`src/hooks/mutation/useMutateStopRecording.ts`)
 *     returns early on it TWICE — once on entry, and again after its
 *     `get_recording_state()` await — so the safety net that exists precisely to
 *     kill a runaway recorder is switched off by our own fix's residue, for
 *     every genuine runaway afterwards.
 *
 * WHAT COUNTS AS "a recording is in progress" is not a list invented here. It is
 * `isRecordingInProgress` (`src/hooks/desktop/useChronicleSessionControl.ts`),
 * the repo's own three-layer reader, plus `activeSessionIdAtom`, the synchronous
 * "recording is RIGHT NOW happening in this tab" signal that
 * `isRecordingActiveForWorkspaceSwitch` consults BEFORE those three layers.
 * This function is the inverse of that set, minus the layers our sweep does not
 * stop:
 *
 *  - `timerModel.isRunning` (+ `seconds`)              — cleared.
 *  - `activeSessionIdAtom`                              — cleared.
 *  - `activeRecordingTransportAtom`                     — cleared, so the next
 *    recording routes by the input selector again rather than by a dead stamp
 *    (the same release a real stop performs in `useMutateStopRecording`'s
 *    `onMutate`).
 *  - cached `recording_status` on `GET_SESSION_BY_ID`   — set to `IDLE`.
 *  - `chronicleSessionIdAtom` / `offlineCaptureRunningAtom` — deliberately NOT
 *    written. The sweep stops the ONLINE chunk upload; a Chronicle device
 *    session and an offline capture are different engines it never addressed,
 *    and clearing them would claim a stop that never happened. They ARE read,
 *    as a veto: see {@link anotherCaptureEngineIsLive}, which stands the release
 *    down whenever one of those engines is capturing, because releasing the
 *    SHARED timer and `activeSessionIdAtom` would tear the chrome off it.
 *
 * The write set is the same one `rollbackOptimisticRecording`
 * (`useMutateStartTauriRecording.ts`) performs when a start aborts — the atoms
 * plus the cached status — with the timer added, because there the start never
 * ran the timer and here the recording did.
 */

type JotaiStore = ReturnType<typeof getDefaultStore>;

const RELEASED_EVENT = 'DESKTOP_RECORDING_SESSION_END_SWEEP_STATE_RELEASED';
const NOT_OURS_EVENT = 'DESKTOP_RECORDING_SESSION_END_SWEEP_STATE_NOT_OURS';
const OTHER_ENGINE_EVENT = 'DESKTOP_RECORDING_SESSION_END_SWEEP_STATE_OTHER_ENGINE_LIVE';

/**
 * Is a capture engine THIS SWEEP NEVER ADDRESSED still running?
 *
 * **The veto is on this condition alone — not on the sweep's outcome, and not on
 * its basis.** That is the whole rule, and it is deliberately blunt: do not
 * release SHARED recording state while another capture engine is live, however
 * we arrived here.
 *
 * It used to be narrower, and the narrowing was the bug. The predicate was
 * `basis: 'unprobed'` + `outcome: 'nothingCurrent'` — one path, chosen because
 * that path was the one being written at the time — while `outcome: 'stopped'`
 * entitled release UNCONDITIONALLY. So the single most dangerous shape walked
 * straight past the guard built for exactly this harm. A guard aimed at a path
 * cannot cover a property; this one is stated over the property.
 *
 * WHY A CONFIRMED STOP CHANGES NOTHING HERE. The sweep stops the ONLINE CHUNK
 * UPLOAD and only that: every production caller of `claim_at_start` is on the
 * online-chunk path, so `current_identity()` can only ever name an online-chunk
 * recording — never a Chronicle device session, never an offline capture. A
 * confirmed terminal therefore proves an online-chunk recording ended. It proves
 * nothing whatever about the other two engines, and the state released below is
 * SHARED — one timer, one `activeSessionIdAtom`. Releasing it while one of them
 * is capturing tears the recording chrome off a recording that is genuinely
 * running: the stop-a-different-recording class of harm FR-038 exists to
 * prevent, arriving from our own fix.
 *
 * DO NOT RE-LITIGATE THE OTHER SIDE. The competing harm is a stale
 * `timerModel.isRunning` disabling `runRecorderSafetyStop` for the next genuine
 * runaway, and it does NOT overlap this condition. If another engine is live,
 * then a recording really is in progress: `isRunning` true is CORRECT, and the
 * safety stop standing aside is CORRECT, because a live recording owns the
 * recorder. The two harms are mutually exclusive on exactly this predicate,
 * which is why widening it costs nothing on the other side.
 *
 * Both atoms are READ here and never written. The rule that the release must not
 * touch them is unchanged — reading a signal in order to stand aside is not
 * clearing it.
 */
function anotherCaptureEngineIsLive(jotaiStore: JotaiStore): boolean {
  return (
    jotaiStore.get(offlineCaptureRunningAtom) ||
    jotaiStore.get(chronicleSessionIdAtom) !== null
  );
}

/**
 * Did this sweep establish that nothing is recording any more, to the standard
 * that entitles a caller to drop the frontend's recording claim?
 *
 * Answered per OUTCOME, and every member is decided here rather than left to a
 * default, because this predicate is the join between the two halves of the fix
 * and a silent mistake would hide in it.
 *
 *  - `'stopped'`        — TRUE. Native confirmed the terminal of the stop the
 *    sweep itself issued, proved by the `StopReason` that came back.
 *  - `'nothingCurrent'` — TRUE, and it is the STRONGEST of the three: no
 *    online-chunk recording is current. Either native answered
 *    `SELF_HEALING_STALE_EPOCH` (nothing installed, or a different epoch owns
 *    the pipeline), or it replayed an already-terminal epoch's cached outcome.
 *    Both say the recording the sweep could have reached is already finished,
 *    which is better evidence that nothing is recording than a stop is.
 *
 *    An earlier revision of this comment said `SELF_HEALING_STALE_EPOCH` covers
 *    the already-terminal case too. It does not — that path returns `Ok`, and
 *    `session-end-sweep.ts` now classifies it by reading the returned reason.
 *  - `'notConfirmed'`   — TRUE. The CLIENT's bound expired, not the stop: the
 *    IPC is never cancelled and Rust keeps converging past it (see
 *    `SESSION_END_SWEEP_TIMEOUT_MS`). Holding the claim for a stop that is
 *    landing anyway leaves `isRunning` true, which is exactly the state that
 *    disables `runRecorderSafetyStop` for the NEXT runaway.
 *  - `'error'`          — FALSE. Native answered and REFUSED the stop, so
 *    capture may still be live and saying "not recording" would be a lie the UI
 *    cannot recover from.
 *
 * `basis` does NOT change the answer HERE, and that is deliberate. An
 * `'unprobed'` stop — one issued after a dead probe under `anyActiveUpload` —
 * resolves to the same `current_identity()` epoch a probed stop would have
 * named, so a terminal confirmed on it is confirmed on the same recording. What
 * the dead probe cost is knowledge of WHETHER something was live, not of WHICH.
 *
 * That cost is real, though, and it is paid one predicate over rather than
 * inside this one: {@link anotherCaptureEngineIsLive} vetoes the release
 * whenever an engine the sweep never addressed is still capturing, on ANY
 * result. Entitlement and veto are kept apart on purpose — this function answers
 * "did native confirm a stop", not "is it safe to write".
 *
 * FALSE for every `attempted: false` result, including `probeFailed`. No stop
 * IPC was ever addressed to native, so nothing was stopped and there is nothing
 * to release.
 */
function confirmsAStop(result: SessionEndSweepResult): boolean {
  if (!result.attempted) return false;
  return result.outcome !== 'error';
}

/**
 * @param sessionId The session whose end is being released, for an `onlySession`
 *   caller. `null` for an `anyActiveUpload` caller, whose contract is that the
 *   user's involvement with WHATEVER is recording is ending, so whichever session
 *   the frontend still claims is the one to release.
 * @returns whether the claim was released, so a caller (and a test) can assert on
 *   it rather than infer it.
 */
export function releaseRecordingStateAfterSweep({
  result,
  sessionId,
  jotaiStore,
  queryClient,
}: {
  result: SessionEndSweepResult;
  sessionId: string | null;
  jotaiStore: JotaiStore;
  queryClient: QueryClient;
}): boolean {
  if (!confirmsAStop(result)) return false;

  // No release of SHARED recording state while another capture engine is live —
  // whatever outcome we arrived at, and whatever the basis. See
  // {@link anotherCaptureEngineIsLive}: the condition is the rule, and a
  // confirmed stop of the ONLINE chunk upload is not an exception to it.
  if (anotherCaptureEngineIsLive(jotaiStore)) {
    logger.info(OTHER_ENGINE_EVENT, {
      sessionId,
      offlineCaptureRunning: jotaiStore.get(offlineCaptureRunningAtom),
      chronicleSessionLive: jotaiStore.get(chronicleSessionIdAtom) !== null,
      // The result is now log context rather than a condition. It is carried so
      // the fleet can still see WHICH outcomes this veto is standing down — the
      // widening from one shape to the property is visible in the data, not only
      // in this file.
      basis: result.attempted ? result.basis : null,
      outcome: result.attempted ? result.outcome : null,
    });
    return false;
  }

  const claimedSessionId = jotaiStore.get(activeSessionIdAtom);

  // The one case we stand aside: an `onlySession` caller stopped session A while
  // the frontend's live claim names a DIFFERENT session B. B's recording is
  // somebody else's — on a shared desktop, another patient's — and the timer and
  // the transport stamp are SHARED singletons, so clearing them would tear down
  // a live recording we were never addressing. `rollbackOptimisticRecording`
  // guards the same atoms the same way and for the same reason.
  if (sessionId !== null && claimedSessionId !== null && claimedSessionId !== sessionId) {
    logger.info(NOT_OURS_EVENT, { sessionId, claimedSessionId });
    return false;
  }

  stopSharedRecordingTimer();
  jotaiStore.set(activeSessionIdAtom, null);
  jotaiStore.set(activeRecordingTransportAtom, null);

  // Both ids, deduped: the session the caller was ending and the one the
  // frontend still claimed. They are normally the same, and when they are not
  // (an `anyActiveUpload` caller has no id of its own) each row is one the UI
  // would otherwise keep reading as recording.
  const rows = [sessionId, claimedSessionId].filter(
    (id, index, all): id is string => id !== null && all.indexOf(id) === index
  );
  for (const id of rows) {
    queryClient.setQueryData<SessionResponse>(
      [GET_SESSION_BY_ID, { sessionId: id }],
      (old) => (old ? { ...old, recording_status: RecordingStatus.IDLE } : undefined)
    );
  }

  logger.info(RELEASED_EVENT, { sessionId, claimedSessionId });
  return true;
}
